/**
 * As quatro dimensões que a Home recebe como DADO da configuração do PDV:
 * `logoWidth`, `logoHeight`, `buttonsWidth`, `buttonsHeight`.
 *
 * Por que este arquivo existe
 * ---------------------------
 * O resto do PDV já escala com a janela (`src/styles/escala.ts`, `--u`), mas
 * estas quatro não escalavam: elas não são CSS escrito no código, são valor que
 * chega do banco — e chegam em DUAS formas, porque as duas pontas gravam
 * diferente. A Central grava número (`logoWidth: 30`), o slider do PDV grava
 * string (`"200px"`, em Main.modal.tsx). O dump do cliente tem as duas.
 *
 * O QUE A TELA FAZ HOJE com cada forma — esta tabela é o contrato
 * --------------------------------------------------------------
 *
 * | campo | número puro (`30`, `80`)            | string (`"200px"`, `"420px"`)      |
 * |-------|-------------------------------------|------------------------------------|
 * | logo  | `width: 7.5%` — válido, escala      | `Number("200px")` = NaN →          |
 * |       | com a janela                        | `width: NaN%` → declaração         |
 * |       |                                     | DESCARTADA → tamanho natural       |
 * | botão | `width: 80` — CSS inválido,         | `width: 420px` → CONGELA: o botão  |
 * |       | DESCARTADO → o botão fica do        | não encolhe no monitor de 1366     |
 * |       | tamanho do conteúdo, que é          | e em 1920 fica menor que a imagem  |
 * |       | `calc(var(--u)*400)` → já escala    | de dentro (448 dentro de 420)      |
 *
 * O que este módulo conserta: as duas células da direita. O que ele PRESERVA,
 * pixel a pixel: as duas da esquerda.
 *
 * ARMADILHA — não "consertar" o número puro
 * -----------------------------------------
 * É tentador ler `80` como "80px" e emitir `calc(var(--u) * 80)`. NÃO. Hoje
 * `width: 80` no botão não vira nada — o navegador descarta —, então o botão da
 * tela `inicio` mede ~416×406 em 1440. Traduzir para 80px o encolheria para
 * 80×80 na única largura que o gabarito manda preservar intacta. O contrato é o
 * PIXEL de 1440, não a leitura literal do dado. Por isso a normalização
 * CLASSIFICA a forma do registro em vez de colapsar tudo num número, e cada
 * consumidor traduz a classe do jeito que reproduz o pixel de hoje.
 * (`_gabarito-visual/depois/medidas.md`, tela `inicio`: 353,19 / 406 / 454 px.)
 *
 * Regra geral do refactor de escala (a mesma de `escala.ts:20-28`): o NÚMERO não
 * muda. `"420px"` continua valendo 420px em 1440; muda só a forma de expressar,
 * que passa a acompanhar a janela.
 */
import { esc } from "../styles/escala";

export type DimensaoDaConfig =
  /** `"420px"`, `" 200PX "`, `"420.5px"` — medida em px na régua de 1440. */
  | { forma: "px"; px: number }
  /** `80`, `"80"`, `30` — o que a Central grava. */
  | { forma: "numero"; numero: number }
  /** `"50%"`, `"10rem"` — outra unidade de CSS; preserva o que o navegador faz. */
  | { forma: "outro"; css: string }
  /** `""`, `null`, `undefined`, `"abc"`, `NaN` — não declarar nada. */
  | { forma: "ausente" };

const AUSENTE: DimensaoDaConfig = { forma: "ausente" };

/** Número seguido de unidade: `420px`, `50%`, `10rem`, `-3.5em`. */
const NUMERO_COM_UNIDADE = /^([+-]?(?:\d+\.?\d*|\.\d+))\s*([a-z%]+)$/i;

/** Arredonda o que a multiplicação do hover produz (`200 * 4 / 3`). */
const limpar = (n: number) => Math.round(n * 10000) / 10000;

/**
 * Classifica o valor bruto que veio da configuração. Uma função só — antes o
 * mesmo dado era interpretado em lugares diferentes, cada um do seu jeito.
 */
export function lerDimensaoDaConfig(bruto: unknown): DimensaoDaConfig {
  if (typeof bruto === "number") {
    return Number.isFinite(bruto) ? { forma: "numero", numero: bruto } : AUSENTE;
  }
  if (typeof bruto !== "string") return AUSENTE;

  const texto = bruto.trim();
  if (texto === "") return AUSENTE;

  const casou = NUMERO_COM_UNIDADE.exec(texto);
  if (casou) {
    const valor = Number(casou[1]);
    if (!Number.isFinite(valor)) return AUSENTE;
    return casou[2].toLowerCase() === "px"
      ? { forma: "px", px: valor }
      : { forma: "outro", css: texto };
  }

  // Sem unidade: é o número escrito como texto ("80"). `Number("")` já saiu
  // acima; `Number("abc")` é NaN e cai em ausente.
  const numero = Number(texto);
  return Number.isFinite(numero) ? { forma: "numero", numero } : AUSENTE;
}

/**
 * Largura/altura do BOTÃO de acesso da Home.
 * `undefined` significa "não emitir declaração nenhuma" — é o que reproduz o
 * `auto` de hoje, em que o botão toma o tamanho da imagem de dentro.
 */
export function medidaDoBotaoDeAcesso(
  dimensao: DimensaoDaConfig
): string | undefined {
  switch (dimensao.forma) {
    // CONSERTO: era `420px` congelado; agora acompanha a janela valendo o mesmo
    // 420px em 1440.
    case "px":
      return esc(dimensao.px);
    // PRESERVA: hoje `width: 80` é CSS inválido e o navegador descarta.
    case "numero":
      return undefined;
    // PRESERVA: hoje o valor sai cru e quem decide é o navegador.
    case "outro":
      return dimensao.css;
    default:
      return undefined;
  }
}

/**
 * Largura/altura do LOGO da Home.
 * `fator` é 4 no tamanho normal e 3 no `:hover` — é a razão que a tela já usava
 * (`logoWidth / 4` e `logoWidth / 3`), preservada aqui.
 */
export function medidaDoLogo(
  dimensao: DimensaoDaConfig,
  fator: 4 | 3
): string | undefined {
  switch (dimensao.forma) {
    // CONSERTO: era `NaN%`, descartado, e o logo saía em tamanho natural.
    // Em 1440 passa a valer o número do registro; no hover cresce na mesma
    // proporção de antes (4/3). A multiplicação fica em JS, não dentro do calc.
    case "px":
      return esc(limpar((dimensao.px * 4) / fator));
    // PRESERVA exatamente o de hoje: `30` → `7.5%` (e `10%` no hover).
    case "numero":
      return `${limpar(dimensao.numero / fator)}%`;
    // PRESERVA: hoje `Number("50%")` é NaN e a declaração é descartada.
    case "outro":
      return undefined;
    default:
      return undefined;
  }
}

/**
 * Monta a declaração CSS, ou string vazia quando não há valor. Emitir
 * `width: ;` não é a mesma coisa que não emitir nada — a primeira é regra
 * inválida, a segunda deixa o `auto` valer, que é o comportamento de hoje.
 */
export function declaracao(nome: string, valor: string | undefined): string {
  return valor === undefined ? "" : `${nome}: ${valor};`;
}
